import React, { Fragment, useEffect, useState } from "react";
import { backend, API_URL } from "../../../../config";
import {
  Container,
  CssBaseline,
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import "./Print.css";

import Logo from "../../../../assets/images/logo.png";

const useStyles = makeStyles((theme) => ({
  tableBody: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const columns = [
  { id: "unit_quantity", label: "Unit Quantity", align: "center" },
  { id: "case_quantity", label: "Case Quantity", align: "center" },
  { id: "item_code", label: "Item Code", align: "center" },
  { id: "name", label: "Description", align: "left" },
  { id: "unit_price", label: "Unit Price", align: "center" },
  { id: "case_price", label: "Case Price", align: "center" },
  { id: "total", label: "Amount", align: "center" },
];

const Print = (props) => {
  const orderId = props.match.params.id;
  const classes = useStyles();

  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    const getInvoice = async () => {
      await backend
        .get(`${API_URL}orders/${orderId}/invoice`)
        .then(({ data }) => {
          let dataItemsMap = [];

          if (data.items.length) {
            dataItemsMap = data.items.map((item) => {
              if (
                item.subitem_weights.hasOwnProperty("u") ||
                item.subitem_weights.hasOwnProperty("c")
              ) {
                let totalWeightLbs = 0;

                // sum unit weights
                item.subitem_weights.u.forEach(
                  (weight) => (totalWeightLbs += weight)
                );

                // sum case weights
                item.subitem_weights.c.forEach(
                  (weight) => (totalWeightLbs += weight)
                );

                return {
                  ...item,
                  subitem_weight_lbs: totalWeightLbs.toFixed(2),
                };
              } else {
                return { ...item, subitem_weight_lbs: null };
              }
            });
          }

          const dataMap = { ...data, items: dataItemsMap };

          setInvoice(dataMap);
        });
    };

    if (!isNaN(orderId)) {
      getInvoice();
    }
  }, [orderId]);

  const orderItemsCellValue = (row, column) => {
    let value = row[column.id];

    if (typeof value === "boolean") {
      return "" + value;
    }

    if (
      (column.id === "unit_quantity" && value === 0) ||
      (column.id === "case_quantity" && value === 0)
    ) {
      value = "";
    }

    if (
      (column.id.includes("price") || column.id.includes("total")) &&
      value !== null
    ) {
      value =
        "$" +
        value.toFixed(2) +
        `${
          column.id.includes("price") && row["subitem_weight_lbs"] > 0
            ? " / lb"
            : ""
        }`;
    }

    if (
      (column.id === "price_per_unit" && row["unit_quantity"] === 0) ||
      (column.id === "price_per_case" && row["case_quantity"] === 0)
    ) {
      value = "";
    }

    return value;
  };

  return (
    <>
      <CssBaseline />
      <Container>
        <div className="invoice-print">
          <Grid container spacing={3}>
            <Grid item xs={2} alignSelf="center">
              <Typography variant="h6" component="div">
                <img src={Logo} alt="logo" width="100%" height="auto" />
              </Typography>
            </Grid>
            <Grid item xs={6} alignSelf="center">
              Friendly Foods, Inc.
              <br />
              3336 S La Cienega Blvd Ste 113
              <br />
              Los Angeles, CA 90016
              <br />
              323-653-5900
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h6" component="div">
                Invoice
              </Typography>
              <Typography variant="subtitle1">
                <strong>Customer: </strong>
                {invoice ? invoice.company_location_name : "Unknown"}
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Order #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        {invoice && invoice.order_delivery_date.split("T")[0]}
                      </TableCell>
                      <TableCell>{invoice && invoice.order_id}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={1}></Grid>
            <Grid item xs={"auto"}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bill To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ whiteSpace: "pre" }}>
                        {invoice !== null ? (
                          <>
                            {invoice.company_location_name || ""}
                            <br />
                            {invoice.bill_to_address !== null ? (
                              <>{invoice.bill_to_address}</>
                            ) : (
                              <></>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={1}></Grid>
            <Grid item xs={"auto"}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ship To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell style={{ whiteSpace: "pre" }}>
                        {invoice !== null ? (
                          <>
                            {invoice.company_location_name || ""}
                            <br />
                            {invoice.ship_to_address !== null ? (
                              <>{invoice.ship_to_address}</>
                            ) : (
                              <></>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12}>
              <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={`header-${column.id}`}
                          align={column.align}
                          style={{ minWidth: column.minWidth }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice && invoice.items.length ? (
                      <>
                        {invoice.items.map((row, index) => (
                          <Fragment key={`row-${index}`}>
                            <TableRow
                              hover
                              tabIndex={-1}
                              className={classes.tableBody}
                            >
                              {columns.map((column) => {
                                return (
                                  <TableCell
                                    key={`column-${column.id}-row-${index}`}
                                    align={column.align}
                                  >
                                    {orderItemsCellValue(row, column)}
                                    {column.id === "name" &&
                                    row["subitem_weight_lbs"] !== null ? (
                                      <>
                                        <b>{` (${row["subitem_weight_lbs"]} lbs)`}</b>
                                        <br />
                                        {row["subitem_weights"].u.length &&
                                        row["subitem_weights"].c.length ? (
                                          <>
                                            (U) Weight:
                                            {row["subitem_weights"].u.map(
                                              (sItem, sIndex) => (
                                                <Fragment
                                                  key={`column-${column.id}-row-${index}-weight-u-${sIndex}`}
                                                >
                                                  {sIndex === 0
                                                    ? ` ${sItem}`
                                                    : sIndex ===
                                                      row["subitem_weights"].u
                                                        .length
                                                    ? ` ${sItem}`
                                                    : `, ${sItem}`}
                                                </Fragment>
                                              )
                                            )}
                                            <br />
                                            (C) Weight:
                                            {row["subitem_weights"].c.map(
                                              (sItem, sIndex) => (
                                                <Fragment
                                                  key={`column-${column.id}-row-${index}-weight-c-${sIndex}`}
                                                >
                                                  {sIndex === 0
                                                    ? ` ${sItem}`
                                                    : sIndex ===
                                                      row["subitem_weights"].u
                                                        .length
                                                    ? ` ${sItem}`
                                                    : `, ${sItem}`}
                                                </Fragment>
                                              )
                                            )}
                                          </>
                                        ) : row["subitem_weights"].u.length ? (
                                          <>
                                            Weight:
                                            {row["subitem_weights"].u.map(
                                              (sItem, sIndex) => (
                                                <Fragment
                                                  key={`column-${column.id}-row-${index}-weight-u-${sIndex}`}
                                                >
                                                  {sIndex === 0
                                                    ? ` ${sItem}`
                                                    : sIndex ===
                                                      row["subitem_weights"].u
                                                        .length
                                                    ? ` ${sItem}`
                                                    : `, ${sItem}`}
                                                </Fragment>
                                              )
                                            )}
                                          </>
                                        ) : row["subitem_weights"].c.length ? (
                                          <>
                                            Weight:
                                            {row["subitem_weights"].c.map(
                                              (sItem, sIndex) => (
                                                <Fragment
                                                  key={`column-${column.id}-row-${index}-weight-c-${sIndex}`}
                                                >
                                                  {sIndex === 0
                                                    ? ` ${sItem}`
                                                    : sIndex ===
                                                      row["subitem_weights"].c
                                                        .length
                                                    ? ` ${sItem}`
                                                    : `, ${sItem}`}
                                                </Fragment>
                                              )
                                            )}
                                          </>
                                        ) : (
                                          <>{row["catch_weight_lbs"]}</>
                                        )}
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                    {row["surcharges"].length &&
                                    ["name", "case_price", "total"].includes(
                                      column.id
                                    ) ? (
                                      row["surcharges"].map(
                                        (surcharge, surchIndex) => (
                                          <Fragment
                                            key={`column-${column.id}-row-${index}-surcharge-${surchIndex}`}
                                          >
                                            {column.id === "name" ? (
                                              <>
                                                <br />
                                                <div
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  <i>{surcharge.name}</i>
                                                </div>
                                              </>
                                            ) : column.id === "case_price" ? (
                                              <>
                                                <br />
                                                <div
                                                  style={{ textAlign: "right" }}
                                                >
                                                  <i>
                                                    $
                                                    {surcharge.unit_price.toFixed(
                                                      2
                                                    )}
                                                  </i>
                                                </div>
                                              </>
                                            ) : column.id === "total" ? (
                                              <>
                                                <br />
                                                <div
                                                  style={{ textAlign: "right" }}
                                                >
                                                  <i>
                                                    $
                                                    {surcharge.total.toFixed(2)}
                                                  </i>
                                                </div>
                                              </>
                                            ) : (
                                              <></>
                                            )}
                                          </Fragment>
                                        )
                                      )
                                    ) : (
                                      <></>
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </Fragment>
                        ))}
                      </>
                    ) : (
                      <>
                        <TableRow
                          hover
                          tabIndex={-1}
                          className={classes.tableBody}
                        >
                          <TableCell colSpan={7}>
                            There are no picked items yet
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            {invoice && invoice.items.length ? (
              <>
                <Grid
                  item
                  xs={12}
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingBottom: 0,
                  }}
                >
                  Subtotal:
                  {` $${invoice.subtotal.toFixed(2)}`}
                </Grid>
                {invoice.credit_card_fee ? (
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: 0,
                    }}
                  >
                    Credit Card Fee:
                    {` $${(invoice.credit_card_fee || 0).toFixed(2)}`}
                  </Grid>
                ) : (
                  <></>
                )}
                {invoice.refrigerated_delivery_fee ? (
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: 0,
                    }}
                  >
                    Refrigerated Delivery Fee:
                    {` $${(invoice.refrigerated_delivery_fee || 0).toFixed(2)}`}
                  </Grid>
                ) : (
                  <></>
                )}
                {invoice.late_fee ? (
                  <Grid
                    item
                    xs={12}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: 0,
                    }}
                  >
                    Late Fee:
                    {` $${(invoice.late_fee || 0).toFixed(2)}`}
                  </Grid>
                ) : (
                  <></>
                )}
                <Grid
                  item
                  xs={12}
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 8,
                    padding: 0,
                  }}
                >
                  Taxes:
                  {` $${(invoice.tax_total || 0).toFixed(2)}`}
                </Grid>
                <Grid item xs={9}></Grid>
                <Grid
                  item
                  xs={3}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    border: "1px solid #000",
                    paddingLeft: 0,
                    paddingTop: 5,
                    paddingBottom: 5,
                  }}
                >
                  <b>
                    Grand Total:
                    {` $${invoice.grand_total.toFixed(2)}`}
                  </b>
                </Grid>
              </>
            ) : (
              <></>
            )}
          </Grid>
        </div>
      </Container>
    </>
  );
};

export default Print;
